import { useClassContext, ClassContext } from 'contexts/class-context'
import { useContext } from 'react'
import styles from 'css/components/action-dropdown.module.css'
import copyIcon from 'img/dashicons-admin-page.svg'
import downloadIcon from 'img/dashicons-download.svg'
import expandIcon from 'img/dashicons-editor-expand.svg'
import editIcon from 'img/edit.svg'
import trashIcon from 'img/dashicons-trash.svg'
import updateIcon from 'img/plus-blue.svg'
import uploadFileIcon from 'img/upload-cloud-blue.svg';
import React from 'react'

interface ActionDropdownProps {
    actions: {
        label: string
        onClick: () => void
        icon: React.ReactNode
        disabled?: boolean
    }[]
    className?: string
}

const ActionDropdown: React.FC<ActionDropdownProps> = ({ actions, className }) => {
    return (
        <div className={`${styles.actionDropdown} ${className}`}>
            {actions.map((action) => (
                <div 
                    key={action.label} 
                    onClick={action.disabled ? undefined : action.onClick} 
                    className={`${styles.actionDropdown__item} ${action.disabled ? styles.actionDropdown__item__disabled : ''}`}
                >
                    <span className={styles.actionDropdown__item__icon}>{action.icon}</span>
                    {action.disabled ? `${action.label}...` : action.label}
                </div>
            ))}
        </div>
    )
}

export default ActionDropdown

export const ClassActionDropdown = ({ onOpenStudent }: { onOpenStudent: () => void }) => {
    const { handleDownloadXLS, handleDownloadPDF } = useClassContext()

    return (
        <ActionDropdown actions={[
            { label: 'Open Student Data', onClick: onOpenStudent, icon: <img src={expandIcon} alt="Open Student Data" /> },
            { label: 'Download in XLS', onClick: handleDownloadXLS, icon: <img src={downloadIcon} alt="Download in XLS" /> },
            { label: 'Download in PDF', onClick: handleDownloadPDF, icon: <img src={downloadIcon} alt="Download in PDF" /> },
        ]} className={styles.classActionDropdown} />
    )
}

interface DownloadActionDropdownProps {
    onDownloadXLS?: () => void
    onDownloadPDF?: () => void
}

export const DownloadActionDropdown = ({ onDownloadXLS, onDownloadPDF }: DownloadActionDropdownProps = {}) => {
    const classContext = useContext(ClassContext)
    const handleXLS = onDownloadXLS ?? classContext?.handleDownloadXLS ?? (() => {})
    const handlePDF = onDownloadPDF ?? classContext?.handleDownloadPDF ?? (() => {})

    return (
        <ActionDropdown actions={[
            { label: 'Download in XLS', onClick: handleXLS, icon: <img src={downloadIcon} alt="Download in XLS" /> },
            { label: 'Download in PDF', onClick: handlePDF, icon: <img src={downloadIcon} alt="Download in PDF" /> },
        ]} className={styles.downloadDropdown} />
    )
}

export const StudentActionDropdown = ({ onOpenStudent, className }: { onOpenStudent: () => void; className?: string }) => {
    return (
        <ActionDropdown actions={[
            { label: 'Open Lesson Details', onClick: onOpenStudent, icon: <img src={expandIcon} alt="Open Lesson Details" /> },
            { label: 'Download in XLS', onClick: () => console.log('Download in XLS'), icon: <img src={downloadIcon} alt="Download in XLS" /> },
            { label: 'Download in PDF', onClick: () => console.log('Download in PDF'), icon: <img src={downloadIcon} alt="Download in PDF" /> },
        ]} className={className ?? styles.studentActionDropdown}/>
    )
}

interface LessonActionDropdownProps {
    onDuplicateLesson: () => void
    onRenameLesson: () => void
    onDeleteLesson: () => void
    isDuplicating?: boolean
    isRenaming?: boolean
    isDeleting?: boolean
    hasGradedResults?: boolean
    opensUp?: boolean
}

export const LessonActionDropdown = ({ onDuplicateLesson, onRenameLesson, onDeleteLesson, isDuplicating = false, isRenaming = false, isDeleting = false, hasGradedResults = false, opensUp = false }: LessonActionDropdownProps) => {
    return (
        <ActionDropdown actions={[
            {
                label: isRenaming ? 'Renaming' : 'Rename',
                onClick: onRenameLesson,
                icon: <img src={editIcon} alt="Rename" />,
                disabled: isRenaming
            },
            {
                label: isDuplicating ? 'Duplicating' : 'Make a copy',
                onClick: onDuplicateLesson,
                icon: <img src={copyIcon} alt="Make a copy" />,
                disabled: isDuplicating
            },
            {
                label: hasGradedResults ? 'Delete (has grades)' : isDeleting ? 'Deleting' : 'Delete',
                onClick: onDeleteLesson,
                icon: <img src={trashIcon} alt="Delete" />,
                disabled: isDeleting || hasGradedResults
            },
        ]} className={opensUp ? styles.lessonActionDropdownUp : styles.lessonActionDropdown}/>
    )
}

export const UpdateClassActionDropdown = ({
    setShowAddNewStudentOverlay,
    setShowUpdateClassOverlay,
    setShowRenameClassOverlay,
    setShowArchiveConfirm,
    opensUp = false,
}: {
    setShowAddNewStudentOverlay: (show: boolean) => void,
    setShowUpdateClassOverlay: (show: boolean) => void,
    setShowRenameClassOverlay?: (show: boolean) => void,
    setShowArchiveConfirm?: (show: boolean) => void,
    opensUp?: boolean,
}) => {
    return (
        <ActionDropdown actions={[
            { label: 'Add Student', onClick: () => { setShowAddNewStudentOverlay(true) }, icon: <img src={updateIcon} alt="New Student" /> },
            { label: 'Archive Class', onClick: () => { setShowArchiveConfirm?.(true) }, icon: <img src={trashIcon} alt="Archive" /> },
        ]} className={opensUp ? styles.updateClassActionDropdownUp : styles.updateClassActionDropdown} />
    )
}